import { uniq } from "lodash-es";
import moment from "moment";
import { generatePath } from "utils";
import { routes } from "configs";
import { compareCallback, isDSA } from "./utils";
import * as Types from "./types";

export const filterData: Types.FilterData = (data = [], value) => {
  if (!value) {
    return data;
  }
  const lowerCaseValue = value.toLowerCase();

  return data.filter((item) =>
    item.type === "districtStudentAssignment"
      ? item.assignStatus?.toLowerCase().includes(lowerCaseValue) ||
        item.fullName?.toLowerCase().includes(lowerCaseValue) ||
        item.source?.toLowerCase().includes(lowerCaseValue)
      : item.teacherNote?.toLowerCase().includes(lowerCaseValue) ||
        item.assignStatus?.toLowerCase().includes(lowerCaseValue) ||
        moment(item.dueOn)
          .format("LLL")
          ?.toLowerCase()
          .includes(lowerCaseValue) ||
        item.fullName?.toLowerCase().includes(lowerCaseValue) ||
        item.source?.toLowerCase().includes(lowerCaseValue) ||
        item.assignmentLabels?.some((label) =>
          label.toLowerCase().includes(lowerCaseValue),
        ),
  );
};

const getDSACurriculumAreaIds: Types.GetDSACurriculumAreaIds = (assignments) =>
  uniq(
    assignments.reduce<number[]>(
      (acc, dsa) => [...acc, ...dsa.curriculumAreaIds],
      [],
    ),
  ).map(String);

const hasDSACurriculumArea: Types.HasDSACurriculumArea = (
  assignment,
  dsaCurriculumAreaIds,
) =>
  (assignment.type === "studentAssignment"
    ? assignment.teacherSectionAssignment
    : assignment
  )?.curriculumAreaIds?.some((id) => dsaCurriculumAreaIds.includes(String(id)));

const ASSIGNMENT_ID: { [key: string]: string } = {
  studentAssignment: "id",
  teacherSectionAssignment: "teacherSectionAssignmentId",
};

export const concatAssignments: Types.ConcatAssignments = (
  studentId,
  startedAssignments = [],
  notStartedAssignments = [],
  districtStudentAssignments = [],
  cachedAssignments = [],
  deferredTurnedInAssignmentsId = [],
  isOnline,
) => {
  const dsaCurriculumAreaIds = getDSACurriculumAreaIds(
    districtStudentAssignments,
  );

  return [
    ...districtStudentAssignments.map((item) => ({
      ...item,
      assignStatus:
        item.status === "rejected"
          ? "Returned"
          : `${item.status.charAt(0).toUpperCase()}${item.status.slice(1)}`,
    })),
    ...startedAssignments.map((item) => ({
      ...item,
      assignStatus: item.studentNote ? "Returned" : "Started",
    })),
    ...notStartedAssignments.map((item) => ({
      ...item,
      assignStatus: item.returnNotes?.find(
        (note) => note.studentId === studentId,
      )
        ? "Returned"
        : "Assigned",
    })),
  ]
    .sort(compareCallback)
    .filter((item) => {
      if (isDSA(item)) {
        if (isOnline) {
          return true;
        }

        return cachedAssignments.some(({ id }) =>
          id.includes(
            generatePath(routes[item.type], {
              id: item.id,
            }),
          ),
        );
      }

      if (hasDSACurriculumArea(item, dsaCurriculumAreaIds)) {
        return false;
      }

      if (isOnline) {
        const isBefore =
          item.startOn === null || moment(item.startOn).isBefore(moment());
        if (!isBefore) {
          return false;
        }
        const wasTurnInDeferred = deferredTurnedInAssignmentsId.some(
          (deferred) =>
            (deferred as any)[ASSIGNMENT_ID[item.type]].includes(item.id),
        );
        return !wasTurnInDeferred;
      }

      return cachedAssignments.some((cached) =>
        (cached as any)[ASSIGNMENT_ID[item.type]]?.includes(item.id),
      );
    });
};

export const getSectionKey: Types.GetSectionName = (assignment) =>
  assignment.type === "districtStudentAssignment"
    ? `da_${assignment.schoolName}`
    : `ta_${assignment.teacherSectionId}`;

export const getSections: Types.GetSections = (data = []) =>
  data.reduce<ReturnType<Types.GetSections>>((result, item) => {
    if (item.type !== "districtStudentAssignment" && item.teacherSection) {
      return {
        ...result,
        [getSectionKey(item)]: {
          type: item.type,
          name: item.teacherSection.name,
          teachers: uniq([
            ...(result[item.teacherSection.id]?.teachers || []),
            ...(item.teacherSection.teacherNames || []),
          ]),
        },
      };
    }
    if (item.type === "districtStudentAssignment" && item.districtAssignment) {
      return {
        ...result,
        [getSectionKey(item)]: {
          type: item.type,
          name: item.schoolName,
        },
      };
    }
    return result;
  }, {});
